import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { PageHead } from "@/pages/AppLayout";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Truck, ClipboardCheck, Bell, Wrench, CheckCircle2, DollarSign, FileDown, FileSpreadsheet, AlertTriangle } from "lucide-react";

const GOLD = "#FFB800";
const RED = "#FF3B30";
const BLUE = "#3B82F6";
const GREEN = "#22C55E";
const GREY = "#64748B";
const PIE_COLORS = ["#FFB800", "#3B82F6", "#22C55E", "#A855F7", "#F97316", "#EC4899", "#14B8A6", "#64748B"];

const tip = { backgroundColor: "#1A1C23", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontFamily: "monospace", fontSize: 12 };

const aud = (v) => `$${Number(v || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="card p-5 border-t-4" style={{ borderTopColor: accent || GOLD }} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{label}</p>
        <Icon className="w-4 h-4 text-[#94A3B8]" />
      </div>
      <p className="font-mono text-4xl mt-2" style={{ color: accent || GOLD }}>{value}</p>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <h2 className="font-head font-800 uppercase tracking-tight text-lg mb-4">{title}</h2>
      {children}
    </div>
  );
}

export default function Analytics() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(365);

  useEffect(() => { setD(null); api.get(`/analytics?days=${days}`).then(({ data }) => setD(data)); }, [days]);

  const RANGES = [{ v: 30, l: "30 Days" }, { v: 90, l: "90 Days" }, { v: 365, l: "Year" }];
  const rangeLabel = days === 30 ? "30d" : days === 90 ? "90d" : "Year";

  const switchEl = (
    <div className="flex items-center gap-2">
      <div className="flex gap-1 bg-[#0D0E11] border border-white/10 p-1" data-testid="range-switch">
        {RANGES.map((r) => (
          <button key={r.v} onClick={() => setDays(r.v)}
            className={`px-3 h-9 font-head text-xs uppercase tracking-wide transition-colors ${days === r.v ? "bg-[#FFB800] text-black" : "text-[#94A3B8] hover:text-white"}`}
            data-testid={`range-${r.v}`}>{r.l}</button>
        ))}
      </div>
      <a href={`${API}/analytics/report.pdf?days=${days}&token=${localStorage.getItem("ironlog_token")}`}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 h-11 bg-[#FFB800] text-black font-head text-xs uppercase tracking-wide hover:brightness-110 transition-all"
        data-testid="export-pdf-btn">
        <FileDown className="w-4 h-4" /> Export PDF
      </a>
      <a href={`${API}/analytics/report.csv?days=${days}&token=${localStorage.getItem("ironlog_token")}`}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 h-11 border border-white/15 text-white font-head text-xs uppercase tracking-wide hover:border-[#FFB800] hover:text-[#FFB800] transition-all"
        data-testid="export-csv-btn">
        <FileSpreadsheet className="w-4 h-4" /> CSV
      </a>
    </div>
  );

  if (!d) return (
    <div className="p-5 md:p-10" data-testid="analytics-page">
      <PageHead title="Analytics" sub="Your fleet's maintenance at a glance.">{switchEl}</PageHead>
      <p className="font-head text-2xl text-[#FFB800] uppercase tracking-widest">Loading…</p>
    </div>
  );

  const health = [
    { name: "Healthy", value: d.fleet_health.healthy, color: GREEN },
    { name: "Upcoming", value: d.fleet_health.upcoming, color: GOLD },
    { name: "Overdue", value: d.fleet_health.overdue, color: RED },
  ].filter((x) => x.value > 0);

  const woStatus = [
    { name: "Open", value: d.work_orders_by_status.open, color: RED },
    { name: "In Progress", value: d.work_orders_by_status.in_progress, color: GOLD },
    { name: "Done", value: d.work_orders_by_status.done, color: GREEN },
  ].filter((x) => x.value > 0);

  const woPriority = [
    { name: "1 Day", count: d.work_orders_by_priority["1day"], color: RED },
    { name: "2–3 Days", count: d.work_orders_by_priority["2-3days"], color: GOLD },
    { name: "5+ Days", count: d.work_orders_by_priority["5plus"], color: BLUE },
  ];

  const emptyPie = (arr) => arr.length === 0;

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="analytics-page">
      <PageHead title="Analytics" sub="Your fleet's maintenance at a glance.">{switchEl}</PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <Kpi icon={Truck} label="Machines" value={d.kpis.total_machines} accent={GOLD} />
        <Kpi icon={DollarSign} label={`Spend (${rangeLabel})`} value={aud(d.kpis.total_cost)} accent={GREEN} />
        <Kpi icon={ClipboardCheck} label="Overdue Jobs" value={d.kpis.overdue_work_orders} accent={RED} />
        <Kpi icon={Bell} label="Overdue Reminders" value={d.kpis.overdue_reminders} accent={RED} />
        <Kpi icon={Wrench} label={`Services (${rangeLabel})`} value={d.kpis.services_in_range} accent={BLUE} />
        <Kpi icon={CheckCircle2} label="WO Completion" value={`${d.kpis.completion_rate}%`} accent={GREEN} />
      </div>

      {d.budget_alerts && d.budget_alerts.length > 0 && (
        <div className="card p-5 mb-4 border-l-4 border-l-[#FF3B30]" data-testid="budget-alerts">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
            <h2 className="font-head font-800 uppercase tracking-tight text-lg">Over Budget This Month</h2>
          </div>
          <div className="space-y-2">
            {d.budget_alerts.map((b, i) => (
              <div key={i} className="flex items-center justify-between font-mono text-sm border-b border-white/5 pb-2" data-testid={`budget-alert-${i}`}>
                <span className="truncate pr-3">{b.name}</span>
                <span className="text-[#FF3B30] whitespace-nowrap">{aud(b.spent)} / {aud(b.budget)} <span className="text-[#94A3B8]">(+{aud(b.over)})</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Panel title="Fleet Health" testid="chart-fleet-health">
          {emptyPie(health) ? <p className="font-body text-sm text-[#94A3B8]">No machines yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={health} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {health.map((e, i) => <Cell key={i} fill={e.color} stroke="#0D0E11" />)}
                </Pie>
                <Tooltip contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex justify-center gap-4 mt-2">
            {health.map((h) => <span key={h.name} className="font-mono text-xs flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: h.color }} />{h.name} {h.value}</span>)}
          </div>
        </Panel>

        <Panel title={`Services Logged (${rangeLabel})`} testid="chart-services-month">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.services_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tip} cursor={{ fill: "rgba(255,184,0,0.08)" }} />
              <Bar dataKey="count" fill={GOLD} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Work Orders by Status" testid="chart-wo-status">
          {emptyPie(woStatus) ? <p className="font-body text-sm text-[#94A3B8]">No work orders yet.</p> : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={woStatus} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {woStatus.map((e, i) => <Cell key={i} fill={e.color} stroke="#0D0E11" />)}
                  </Pie>
                  <Tooltip contentStyle={tip} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-2">
                {woStatus.map((h) => <span key={h.name} className="font-mono text-xs flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: h.color }} />{h.name} {h.value}</span>)}
              </div>
            </>
          )}
        </Panel>

        <Panel title="Work Orders by Priority" testid="chart-wo-priority">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={woPriority}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tip} cursor={{ fill: "rgba(255,184,0,0.08)" }} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {woPriority.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Machines by Type" testid="chart-machine-type">
          {d.machines_by_type.length === 0 ? <p className="font-body text-sm text-[#94A3B8]">No machines yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.machines_by_type} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="type" width={90} tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tip} cursor={{ fill: "rgba(255,184,0,0.08)" }} />
                <Bar dataKey="count" fill={BLUE} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Top Machines by Services" testid="chart-top-machines">
          {d.top_machines.length === 0 ? <p className="font-body text-sm text-[#94A3B8]">No services logged yet.</p> : (
            <div className="space-y-3 pt-2">
              {d.top_machines.map((m, i) => {
                const max = d.top_machines[0].count || 1;
                return (
                  <div key={i} data-testid={`top-machine-${i}`}>
                    <div className="flex justify-between font-mono text-xs mb-1"><span className="truncate pr-2">{m.name}</span><span className="text-[#FFB800]">{m.count}</span></div>
                    <div className="h-2 bg-white/5"><div className="h-2 bg-[#FFB800]" style={{ width: `${(m.count / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
        <Panel title={`Service Spend (${rangeLabel})`} testid="chart-cost-series">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.cost_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 12, fontFamily: "monospace" }} axisLine={false} tickLine={false} tickFormatter={(v) => aud(v)} width={60} />
              <Tooltip contentStyle={tip} cursor={{ fill: "rgba(34,197,94,0.08)" }} formatter={(v) => [aud(v), "Spend"]} />
              <Bar dataKey="cost" fill={GREEN} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title={`Spend by Machine (${rangeLabel})`} testid="chart-cost-machine">
          {(!d.cost_by_machine || d.cost_by_machine.length === 0) ? <p className="font-body text-sm text-[#94A3B8]">No service costs recorded yet.</p> : (
            <div className="space-y-3 pt-2">
              {d.cost_by_machine.map((m, i) => {
                const max = d.cost_by_machine[0].cost || 1;
                return (
                  <div key={i} data-testid={`spend-machine-${i}`}>
                    <div className="flex justify-between font-mono text-xs mb-1"><span className="truncate pr-2">{m.name}</span><span className="text-[#22C55E]">{aud(m.cost)}</span></div>
                    <div className="h-2 bg-white/5"><div className="h-2 bg-[#22C55E]" style={{ width: `${(m.cost / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel title="Spend by Service Type" testid="chart-cost-type">
          {(!d.cost_by_type || d.cost_by_type.length === 0) ? <p className="font-body text-sm text-[#94A3B8]">No service costs recorded yet.</p> : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={d.cost_by_type} dataKey="cost" nameKey="type" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {d.cost_by_type.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#0D0E11" />)}
                  </Pie>
                  <Tooltip contentStyle={tip} formatter={(v, n) => [aud(v), n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
                {d.cost_by_type.map((t, i) => <span key={t.type} className="font-mono text-xs flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{t.type} {aud(t.cost)}</span>)}
              </div>
            </>
          )}
        </Panel>

        <Panel title="Overdue Downtime — Least Reliable" testid="chart-downtime">
          <p className="font-mono text-[10px] text-[#94A3B8] -mt-3 mb-3">Days work orders have sat past their due date</p>
          {(!d.downtime || d.downtime.length === 0) ? <p className="font-body text-sm text-[#94A3B8]">No overdue work orders. Fleet is on schedule.</p> : (
            <div className="space-y-3 pt-1">
              {d.downtime.map((m, i) => {
                const max = d.downtime[0].days || 1;
                return (
                  <div key={i} data-testid={`downtime-machine-${i}`}>
                    <div className="flex justify-between font-mono text-xs mb-1"><span className="truncate pr-2">{m.name}</span><span className="text-[#FF3B30]">{m.days}d · {m.jobs} job{m.jobs !== 1 ? "s" : ""}</span></div>
                    <div className="h-2 bg-white/5"><div className="h-2 bg-[#FF3B30]" style={{ width: `${(m.days / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
